import React, { useEffect, useState } from 'react';
import {
  Typography,
  Stack,
  Divider,
  Card,
  CardContent,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase/firebase';
import { getCurrentBusinessConfig } from '../../../firebase/firebase-delegate';
import { WEBSITE_ID } from '../../../projectConfig';
import ErrorDialog from '../../../components/global/ErrorDialog';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { employeeCountMap, frontDeskPlan, isDowngrade, planNameMap, plans } from './planConfig';



const ManageSubscriptionPage: React.FC = () => {
  const [checkoutMode, setCheckoutMode] = useState<'employees' | 'front_desk'>('employees');

  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [selectedEmployees, setSelectedEmployees] = useState(1);
  const selectableEmployeeCounts = [1, 2, 3, 4, 5];

  const businessConfigQuery = useQuery({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });

  const paymentInfo = businessConfigQuery.data?.paymentInfo;
  const plan = paymentInfo?.planType ?? 'free';
  const status = paymentInfo?.subscriptionStatus ?? 'inactive';
  const hasExistingSubscription = ['active', 'trialing', 'trial', 'past_due', 'unpaid'].includes(status);
  const currentPeriodEnd = paymentInfo?.currentPeriodEnd
    ? dayjs(paymentInfo.currentPeriodEnd).format('MMMM D, YYYY')
    : null;

  const [redirecting, setRedirecting] = useState(
    !!localStorage.getItem('redirectingToStripe')
  );

  const openStripePortalMutation = useMutation({
    mutationFn: async () => {
      const callable = httpsCallable<
        { websiteId: string; returnUrl: string },
        { url: string }
      >(functions, 'createStripePortal');

      const result = await callable({
        websiteId: WEBSITE_ID,
        returnUrl: window.location.href,
      });
      localStorage.setItem('redirectingToStripe', 'true');

      window.location.href = result.data.url;
    },
  });

  const createStripeCustomerMutation = useMutation({
    mutationFn: async ({ websiteId }: { websiteId: string }) => {
      const callable = httpsCallable<
        { websiteId: string },
        { customerId: string }
      >(functions, 'createStripeCustomer');

      return await callable({ websiteId });
    },
    onSuccess: () => {
      businessConfigQuery.refetch();
    },
  });

  const startCheckoutMutation = useMutation({
    mutationFn: async ({
      planId,
      websiteId,
      returnUrl,
      checkoutMode,
      addOnIds = [],
    }: {
      planId: string;
      websiteId: string;
      returnUrl: string;
      checkoutMode: 'employees' | 'front_desk';
      addOnIds?: string[];
    }) => {
      const callable = httpsCallable<
        { planId: string; websiteId: string; returnUrl: string; checkoutMode: 'employees' | 'front_desk'; addOnIds?: string[] },
        { url: string }
      >(functions, 'startStripeCheckout');

      const result = await callable({ planId, websiteId, returnUrl, checkoutMode, addOnIds });
      localStorage.setItem('redirectingToStripe', 'true');

      window.location.href = result.data.url;
    },
    onError: (err: any) => {
      const message =
        err?.message?.includes('already') || err?.message?.includes('409')
          ? 'You already have an active subscription. Please use the "Manage Subscription" button instead.'
          : 'Something went wrong while starting checkout.';
      setErrorMessage(message);
      setOpenErrorDialog(true);
    },
  });

  const handleStartSubscription = (planId: string, selectedCheckoutMode: 'employees' | 'front_desk') => {
    const currentPlanId = paymentInfo?.planType;
    if (currentPlanId && isDowngrade(currentPlanId, planId)) {
      setErrorMessage("Downgrading your plan is not available through this interface. Please contact support.");
      setOpenErrorDialog(true);
      return;
    }
    if (!paymentInfo?.stripeCustomerId) {
      createStripeCustomerMutation.mutate(
        { websiteId: WEBSITE_ID },
        {
          onSuccess: () => {
            startCheckoutMutation.mutate({
              planId,
              websiteId: WEBSITE_ID,
              returnUrl: window.location.href,
              checkoutMode: selectedCheckoutMode,
            });

          },
          onError: () => {
            setErrorMessage('Failed to create Stripe customer.');
            setOpenErrorDialog(true);
          },
        }
      );
    } else {
      startCheckoutMutation.mutate({
        planId,
        websiteId: WEBSITE_ID,
        returnUrl: window.location.href,
        checkoutMode: selectedCheckoutMode,
      });
    }
  };

  const seatPlanCandidates = plans.filter(
    (candidatePlan) => candidatePlan.id && (employeeCountMap[candidatePlan.id] ?? 0) <= 5
  );
  const getPlanForEmployees = (employeeCount: number) =>
    seatPlanCandidates.find((candidatePlan) => (employeeCountMap[candidatePlan.id] ?? 0) >= employeeCount);
  const selectedPlan = getPlanForEmployees(selectedEmployees);
  const selectedPlanId = selectedPlan?.id ?? '';
  const selectedPlanSeats = selectedPlanId ? employeeCountMap[selectedPlanId] ?? 0 : 0;
  const isSelectedPlanDowngrade = selectedPlanId
    ? isDowngrade(paymentInfo?.planType, selectedPlanId)
    : false;

  useEffect(() => {
    if (redirecting) {
      localStorage.removeItem('redirectingToStripe');
      setRedirecting(false);
    }
  }, []);

  if (redirecting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6" color="text.secondary">
            Possible Changes Detected
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please refresh the page to see updated subscription status.
          </Typography>
        </Stack>
      </Box>
    );
  }


  return (
    <Box sx={{ px: 2, py: 4, maxWidth: 1000, mx: 'auto' }}>
      <ErrorDialog
        open={openErrorDialog}
        onClose={() => setOpenErrorDialog(false)}
        message={errorMessage}
      />

      <Typography variant="h5" fontWeight={700} mb={3}>
        Manage Subscription
      </Typography>

      {!paymentInfo?.skipSubscriptionCheck && (
        <Stack spacing={1.5} mb={4}>
          <Typography color="text.secondary">
            {!currentPeriodEnd ? (
              <>You haven't started a subscription yet. Please select a plan to activate your website.</>
            ) : hasExistingSubscription && planNameMap[plan] ? (
              <>Currently on the <strong>{planNameMap[plan]}</strong>.</>
            ) : (
              <>Not enrolled in a plan. Website will be active until <strong>{currentPeriodEnd}</strong>.</>
            )}
          </Typography>


          <Stack direction="row" spacing={2}>
            <Typography><strong>Status:</strong> {status}</Typography>
            {currentPeriodEnd && (
              <Typography>
                <strong>{status === 'trialing' ? 'Trial Ends:' : 'Billing Ends:'}</strong> {currentPeriodEnd}
              </Typography>
            )}
          </Stack>

          <Box
            sx={{
              mt: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              px: 2,
              py: 1.5,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              All plans include
            </Typography>
            <Stack spacing={0.25}>
              <Typography variant="body2" color="text.secondary">✅ Custom booking website</Typography>
              <Typography variant="body2" color="text.secondary">✅ Employee Portal</Typography>
              <Typography variant="body2" color="text.secondary">✅ Online Payments</Typography>
              <Typography variant="body2" color="text.secondary">✅ SMS/Email confirmations for customers and employees</Typography>
              <Typography variant="body2" color="text.secondary">✅ Custom domain (yourname.scheduify.com)</Typography>
              <Typography variant="body2" color="text.secondary">✅ Scheduify Customer Support</Typography>
            </Stack>
          </Box>
        </Stack>
      )}

      <Divider sx={{ mb: 4 }} />

      {hasExistingSubscription ? (
        <Box textAlign="center">
          <Button
            variant="contained"
            onClick={() => openStripePortalMutation.mutate()}
            sx={{ py: 1.5, fontWeight: 600, minWidth: 250 }}
          >
            Manage Subscription
          </Button>
        </Box>
      ) : (
        <Stack spacing={3}>
          {paymentInfo?.skipSubscriptionCheck ? (
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold">
                  No Subscription Required
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Your site is currently configured to run without a subscription. No action is needed at this time.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                <Stack spacing={3} alignItems="center" textAlign="center">
                  <Stack spacing={1}>
                    <Typography variant="h6" fontWeight={700}>
                      Choose your subscription plan
                    </Typography>
                    <Typography color="text.secondary">
                      Select employee count or choose Front Desk Mode.
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} width="100%" maxWidth={420}>
                    <Button
                      fullWidth
                      variant={checkoutMode === 'employees' ? 'contained' : 'outlined'}
                      color="secondary"
                      onClick={() => setCheckoutMode('employees')}
                    >
                      Employees
                    </Button>
                    <Button
                      fullWidth
                      variant={checkoutMode === 'front_desk' ? 'contained' : 'outlined'}
                      color="secondary"
                      onClick={() => setCheckoutMode('front_desk')}
                    >
                      Front Desk Mode
                    </Button>
                  </Stack>

                  {checkoutMode === 'employees' ? (
                    <>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <IconButton
                          aria-label="Decrease employee count"
                          onClick={() =>
                            setSelectedEmployees((prev) => {
                              const currentIndex = selectableEmployeeCounts.indexOf(prev);
                              const nextIndex = Math.max(0, currentIndex - 1);
                              return selectableEmployeeCounts[nextIndex];
                            })
                          }
                          disabled={selectedEmployees === selectableEmployeeCounts[0]}
                          color="secondary"
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <RemoveIcon />
                        </IconButton>

                        <Stack minWidth={170}>
                          <Typography variant="overline" color="text.secondary">
                            Employees
                          </Typography>
                          <Typography variant="h4" fontWeight={700}>
                            {selectedEmployees}
                          </Typography>
                        </Stack>

                        <IconButton
                          aria-label="Increase employee count"
                          onClick={() =>
                            setSelectedEmployees((prev) => {
                              const currentIndex = selectableEmployeeCounts.indexOf(prev);
                              const nextIndex = Math.min(selectableEmployeeCounts.length - 1, currentIndex + 1);
                              return selectableEmployeeCounts[nextIndex];
                            })
                          }
                          disabled={selectedEmployees === selectableEmployeeCounts[selectableEmployeeCounts.length - 1]}
                          color="secondary"
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>

                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {selectedPlan ? `${selectedPlan.title} ${selectedPlan.price}` : 'No plan available'}
                        </Typography>
                        {selectedPlanSeats > selectedEmployees && (
                          <Typography variant="body2" color="text.secondary">
                            Includes up to {selectedPlanSeats} employees
                          </Typography>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Stack spacing={1} alignItems="center">
                      <Typography variant="subtitle1" fontWeight={600}>
                        {frontDeskPlan ? `${frontDeskPlan.title} ${frontDeskPlan.price}` : 'Front Desk Mode plan unavailable'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        1 admin login, orchestrator booking flow, and front-desk notification routing.
                      </Typography>
                    </Stack>
                  )}

                  <Stack spacing={1} width="100%" maxWidth={360}>
                    {checkoutMode === 'employees' ? (
                      <>
                        <Button
                          fullWidth
                          variant="contained"
                          color="secondary"
                          disabled={!selectedPlanId || isSelectedPlanDowngrade}
                          onClick={() => selectedPlanId && handleStartSubscription(selectedPlanId, 'employees')}
                          sx={{ py: 1 }}
                        >
                          {isSelectedPlanDowngrade ? 'DOWNGRADE UNAVAILABLE' : 'SELECT PLAN'}
                        </Button>
                        {isSelectedPlanDowngrade && (
                          <Typography variant="caption" color="text.secondary" textAlign="center">
                            Downgrading is unavailable here. Contact Scheduify support to change your plan.
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        disabled={!frontDeskPlan?.id}
                        onClick={() => {
                          if (!frontDeskPlan?.id) {
                            return;
                          }
                          handleStartSubscription(frontDeskPlan.id, 'front_desk');
                        }}
                        sx={{ py: 1 }}
                      >
                        {frontDeskPlan?.id ? 'SELECT FRONT DESK MODE' : 'FRONT DESK PLAN NOT CONFIGURED'}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}
    </Box>
  );


};

export default ManageSubscriptionPage;
